import { useCallback, useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router';

import { apiMarkUserAsOnboarded } from '#/api/vm/apiVMOnboardingEndpoints';
import {
  useUserEnvironment,
  useUserEnvironmentActions,
} from '#/contexts/UserEnvironmentContext';
import {
  authLoginRoute,
  authSignupRoute,
  authUserInviteRoute,
} from '#/modules/auth/routing/auth.routes';
import { useRouteIsCmsApiPage } from '#/modules/cms/hooks/useRouteIsCmsApiPage';
import { salariesExploreRoute } from '#/modules/salary/routing/salary.routes';
import { userDashboardApiEnableProfileFeatures } from '#/modules/userDashboard/api/userDashboardApiEnableProfileFeatures';
import { userDashboardApiSaveIntakeIntents } from '#/modules/userDashboard/api/userDashboardApiSaveIntakeIntents';
import { deleteCookie, getCookieValue } from '#/utils/cookies';
import { onHomepage } from '#/utils/intakeintents';
import { hideModal, showModal, useModal } from '#/zustand-stores/modalStore';
import { usePreSignupComment } from '#/zustand-stores/preSignupCommentStore';
import { usePreSignupFollow } from '#/zustand-stores/preSignupFollowStore';
import { usePreSignupReaction } from '#/zustand-stores/preSignupReactionStore';
import { usePreSignupRsvp } from '#/zustand-stores/preSignupRsvpStore';

// Only users created after this date should see
// the intake intents modal
const INTAKE_INTENTS_START_DATE = new Date('2023-08-15');

// Routes where modals should never appear (regardless of user state)
const ALWAYS_DISABLE_MODAL_ROUTES = [authUserInviteRoute.fullPath];

// Routes where modals should not appear for logged-out users
const DISABLE_MODAL_ROUTES_FOR_GUESTS = [salariesExploreRoute.fullPath];

export function ShowUserModalsGlobalHandler() {
  const { user, isUserLoaded } = useUserEnvironment();

  const { updateUserPartially } = useUserEnvironmentActions();

  const location = useLocation();

  const { modal } = useModal();
  const currentModalType = modal?.type;

  const {
    created,
    email,
    emailVerified,
    firstName,
    intakeIntentsDatetimeSet,
    lastName,
    orgs,
    showNotifPrefModal,
    hasIdealistProfileFeature,
    hasPublishedIdealistProfile,
    authProviders,
  } = user ?? {};

  const profileWizardModalAlreadyShownRef = useRef(false);
  const routeIsCmsApiPage = useRouteIsCmsApiPage();
  const { preSignupReaction } = usePreSignupReaction();
  const { hasPreSignupComment } = usePreSignupComment();
  const { preSignupFollowUserId } = usePreSignupFollow();
  const { preSignupRsvpAnswer, preSignupRsvpEventId } = usePreSignupRsvp();

  const verify = useCallback(() => {
    const justSignedUpModalCookie = getCookieValue('justSignedUpModal');
    const userDismissedNotificationPreferencesCookie = getCookieValue(
      'userDismissedNotificationPreferencesDialog',
    );

    const isAccountSettingsPage =
      location.pathname === '/dashboard/account-settings';
    const isLoginOrSignupPage = ['signup', authLoginRoute.with({})].includes(
      location.pathname,
    );
    const isIOSMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App',
    );
    const noModalPresent = !currentModalType;
    const informationModalPresent =
      currentModalType === 'INFORMATION_REQUIRED' && noModalPresent;

    // INFORMATION_REQUIRED expected for case of Facebook user not allowing
    // their email address and then has just filled one in.
    const emailNeedsVerification =
      Boolean(email) &&
      !emailVerified &&
      !justSignedUpModalCookie &&
      (noModalPresent || informationModalPresent);

    const hasNonVmOrg = orgs?.some((org) => !org.fromVm);

    const isNonMergedVMUser = user?.vmOnboarding?.type === 'IMPORTED';

    const showVmPostVerificationInfoRequiredModal =
      isNonMergedVMUser &&
      ((!user.hasPassword && authProviders?.length === 0) ||
        user.orgs.length === 0) &&
      noModalPresent;

    const showVmOrgAdminWelcomeModal =
      user?.vmOnboarding &&
      (user.hasPassword || (authProviders && authProviders.length > 0)) &&
      orgs &&
      orgs.length > 0 &&
      !hasNonVmOrg &&
      noModalPresent;

    const needsToFinishUserProfile = (() => {
      if (hasPublishedIdealistProfile) return false;

      // Pre-signup reaction
      if (
        preSignupReaction &&
        routeIsCmsApiPage &&
        preSignupReaction.postId === routeIsCmsApiPage.id
      )
        return true;

      // Pre-signup comment
      if (hasPreSignupComment) return true;

      // Pre-signup follow user action
      if (preSignupFollowUserId) return true;

      // Pre-signup rsvp
      if (preSignupRsvpAnswer && preSignupRsvpEventId) return true;

      return false;
    })();

    const informationRequired =
      (!firstName || !lastName || !email) &&
      !isAccountSettingsPage &&
      noModalPresent;

    const showSignupModal =
      isIOSMobileApp &&
      !isLoginOrSignupPage &&
      noModalPresent &&
      !user &&
      isUserLoaded;

    const showIntakeIntentModal =
      (user?.fromVM === 'IMPORTED' ||
        (created && new Date(created) > INTAKE_INTENTS_START_DATE)) &&
      !intakeIntentsDatetimeSet &&
      noModalPresent &&
      onHomepage(location.pathname);

    const shouldFinishOAuth = justSignedUpModalCookie && noModalPresent;

    const showUpdateNotificationPreferencesModal =
      showNotifPrefModal &&
      noModalPresent &&
      !userDismissedNotificationPreferencesCookie;

    if (showSignupModal) {
      showModal('SIGNUP', { uncloseable: true });
      return;
    }

    if (emailNeedsVerification) {
      showModal('VERIFY_EMAIL', {});
      return;
    }

    // If verification email persists in another browser tab after
    // verification, close it
    // @NOTE: Polling occurs in VerifyEmailModal
    if (currentModalType === 'VERIFY_EMAIL' && emailVerified) {
      hideModal();
      return;
    }

    if (showVmPostVerificationInfoRequiredModal) {
      showModal('VM_POST_VERIFICATION_INFORMATION_REQUIRED', {});
      return;
    }

    if (showVmOrgAdminWelcomeModal) {
      showModal('VM_ORG_ADMIN_WELCOME', {
        onClose: () => {
          void apiMarkUserAsOnboarded();
          updateUserPartially({ vmOnboarding: null });
          hideModal();
        },
      });
      return;
    }

    if (
      needsToFinishUserProfile &&
      emailVerified &&
      currentModalType !== 'CREATE_USER_PROFILE'
    ) {
      if (!hasIdealistProfileFeature) {
        void userDashboardApiEnableProfileFeatures().then(() => {
          showModal('CREATE_USER_PROFILE', {});
          return;
        });
      } else {
        showModal('CREATE_USER_PROFILE', {});
      }
      return;
    }

    if (informationRequired) {
      showModal('INFORMATION_REQUIRED', {});
      return;
    }

    if (shouldFinishOAuth) {
      if (
        ['Google', 'Facebook', '"Apple ID"'].includes(justSignedUpModalCookie)
      ) {
        showModal('FINISH_OAUTH_SIGNUP', {
          onClose: () => deleteCookie('justSignedUpModal'),
        });
      } else {
        deleteCookie('justSignedUpModal');
      }

      return;
    }

    if (showUpdateNotificationPreferencesModal) {
      showModal('NOTIFICATION_PREFERENCES', {});
      return;
    }

    if (showIntakeIntentModal) {
      // If an org has signed up via post a listing,
      // automatically save their posting intention
      // once they have created an organization
      if (orgs && orgs.length > 0) {
        void userDashboardApiSaveIntakeIntents({
          intakeIntents: ['POST_JOB_OR_OTHER_OPP'],
        });
      } else {
        showModal('INTAKE_INTENTS', {});
        return;
      }
    }

    if (
      noModalPresent &&
      user?.hasIdealistProfileFeature &&
      !user.hasPublishedIdealistProfile &&
      onHomepage(location.pathname) &&
      user.showIdealistProfileOnboarding !== false &&
      !profileWizardModalAlreadyShownRef.current
    ) {
      profileWizardModalAlreadyShownRef.current = true;
      showModal('PROFILE_WIZARD', {});
    }
  }, [
    location.pathname,
    currentModalType,
    email,
    emailVerified,
    hasPublishedIdealistProfile,
    preSignupReaction,
    routeIsCmsApiPage,
    hasPreSignupComment,
    preSignupFollowUserId,
    preSignupRsvpAnswer,
    preSignupRsvpEventId,
    firstName,
    lastName,
    user,
    isUserLoaded,
    created,
    intakeIntentsDatetimeSet,
    showNotifPrefModal,
    hasIdealistProfileFeature,
    orgs,
    authProviders,
    updateUserPartially,
  ]);

  useEffect(() => {
    const isIOSMobileApp = window.navigator.userAgent.includes(
      'Idealist Mobile App',
    );

    const isAlwaysDisabledRoute = ALWAYS_DISABLE_MODAL_ROUTES.some((route) =>
      matchPath(route, location.pathname),
    );

    // Skip modal check when on signup page with invite param
    // (user is about to be redirected to accept page)
    const isSignupWithInvite =
      matchPath(authSignupRoute.fullPath, location.pathname) &&
      new URLSearchParams(location.search).has('invite');

    if (isAlwaysDisabledRoute || isSignupWithInvite) return;

    const isGuestDisabledRoute = DISABLE_MODAL_ROUTES_FOR_GUESTS.includes(
      location.pathname,
    );

    if ((!user && !isIOSMobileApp) || isGuestDisabledRoute) return;

    verify();
  }, [verify, user, location]);

  return null;
}
